import { feature, mergeArcs } from 'topojson-client';
import type { Topology } from 'topojson-specification';

// eslint-disable-next-line @typescript-eslint/no-require-imports, @typescript-eslint/no-var-requires
const worldTopology = require('world-atlas/countries-110m.json') as Topology;

/**
 * ISO 3166-1 numeric code (as returned by world-atlas feature.id) → alpha-2 code.
 * Covers all ~177 countries included in the 110m resolution dataset.
 */
const NUMERIC_TO_ISO2: Record<string, string> = {
  '4': 'AF',  '8': 'AL',  '12': 'DZ',  '24': 'AO',  '31': 'AZ',
  '32': 'AR', '36': 'AU', '40': 'AT',  '48': 'BH',  '50': 'BD',
  '51': 'AM', '56': 'BE', '64': 'BT',  '68': 'BO',  '70': 'BA',
  '72': 'BW', '76': 'BR', '96': 'BN',  '100': 'BG', '104': 'MM',
  '108': 'BI', '112': 'BY', '116': 'KH', '120': 'CM', '124': 'CA',
  '132': 'CV', '140': 'CF', '144': 'LK', '148': 'TD', '152': 'CL',
  '156': 'CN', '170': 'CO', '174': 'KM', '178': 'CG', '180': 'CD',
  '188': 'CR', '191': 'HR', '192': 'CU', '196': 'CY', '203': 'CZ',
  '204': 'BJ', '208': 'DK', '214': 'DO', '218': 'EC', '222': 'SV',
  '226': 'GQ', '231': 'ET', '232': 'ER', '233': 'EE', '242': 'FJ',
  '246': 'FI', '250': 'FR', '262': 'DJ', '266': 'GA', '268': 'GE',
  '270': 'GM', '275': 'PS', '276': 'DE', '288': 'GH', '300': 'GR',
  '304': 'GL', '320': 'GT', '324': 'GN', '328': 'GY', '332': 'HT',
  '336': 'VA', '340': 'HN', '348': 'HU', '352': 'IS', '356': 'IN',
  '360': 'ID', '364': 'IR', '368': 'IQ', '372': 'IE', '376': 'IL',
  '380': 'IT', '384': 'CI', '388': 'JM', '392': 'JP', '398': 'KZ',
  '400': 'JO', '404': 'KE', '408': 'KP', '410': 'KR', '414': 'KW',
  '417': 'KG', '418': 'LA', '422': 'LB', '426': 'LS', '428': 'LV',
  '430': 'LR', '434': 'LY', '438': 'LI', '440': 'LT', '442': 'LU',
  '450': 'MG', '454': 'MW', '458': 'MY', '462': 'MV', '466': 'ML',
  '470': 'MT', '478': 'MR', '480': 'MU', '484': 'MX', '492': 'MC',
  '496': 'MN', '498': 'MD', '504': 'MA', '508': 'MZ', '512': 'OM',
  '516': 'NA', '520': 'NR', '524': 'NP', '528': 'NL', '548': 'VU',
  '554': 'NZ', '558': 'NI', '562': 'NE', '566': 'NG', '578': 'NO',
  '583': 'FM', '586': 'PK', '591': 'PA', '598': 'PG', '600': 'PY',
  '604': 'PE', '608': 'PH', '616': 'PL', '620': 'PT', '624': 'GW',
  '626': 'TL', '634': 'QA', '642': 'RO', '643': 'RU', '646': 'RW',
  '659': 'KN', '662': 'LC', '670': 'VC', '674': 'SM', '678': 'ST',
  '682': 'SA', '686': 'SN', '688': 'RS', '690': 'SC', '694': 'SL',
  '703': 'SK', '705': 'SI', '706': 'SO', '710': 'ZA', '716': 'ZW',
  '724': 'ES', '728': 'SS', '729': 'SD', '740': 'SR', '748': 'SZ',
  '752': 'SE', '756': 'CH', '760': 'SY', '762': 'TJ', '764': 'TH',
  '768': 'TG', '776': 'TO', '780': 'TT', '784': 'AE', '788': 'TN',
  '792': 'TR', '795': 'TM', '800': 'UG', '804': 'UA', '807': 'MK',
  '818': 'EG', '826': 'GB', '834': 'TZ', '840': 'US', '858': 'UY',
  '860': 'UZ', '862': 'VE', '882': 'WS', '887': 'YE', '894': 'ZM',
  // entries absent from the original mapping
  '44': 'BS', '84': 'BZ', '90': 'SB', '158': 'TW', '238': 'FK',
  '260': 'TF', '499': 'ME', '540': 'NC', '630': 'PR', '704': 'VN',
  '732': 'EH', '854': 'BF',
};

/** ISO 3166-1 alpha-2 → English country name for every country in the 110m dataset. */
const ISO2_TO_NAME: Record<string, string> = {
  AF: 'Afghanistan', AL: 'Albania', DZ: 'Algeria', AO: 'Angola', AZ: 'Azerbaijan',
  AR: 'Argentina', AU: 'Australia', AT: 'Austria', BH: 'Bahrain', BD: 'Bangladesh',
  AM: 'Armenia', BE: 'Belgium', BT: 'Bhutan', BO: 'Bolivia', BA: 'Bosnia and Herzegovina',
  BW: 'Botswana', BR: 'Brazil', BN: 'Brunei', BG: 'Bulgaria', MM: 'Myanmar',
  BI: 'Burundi', BY: 'Belarus', KH: 'Cambodia', CM: 'Cameroon', CA: 'Canada',
  CV: 'Cape Verde', CF: 'Central African Republic', LK: 'Sri Lanka', TD: 'Chad', CL: 'Chile',
  CN: 'China', CO: 'Colombia', KM: 'Comoros', CG: 'Republic of the Congo', CD: 'DR Congo',
  CR: 'Costa Rica', HR: 'Croatia', CU: 'Cuba', CY: 'Cyprus', CZ: 'Czech Republic',
  BJ: 'Benin', DK: 'Denmark', DO: 'Dominican Republic', EC: 'Ecuador', SV: 'El Salvador',
  GQ: 'Equatorial Guinea', ET: 'Ethiopia', ER: 'Eritrea', EE: 'Estonia', FJ: 'Fiji',
  FI: 'Finland', FR: 'France', DJ: 'Djibouti', GA: 'Gabon', GE: 'Georgia',
  GM: 'Gambia', PS: 'Palestine', DE: 'Germany', GH: 'Ghana', GR: 'Greece',
  GL: 'Greenland', GT: 'Guatemala', GN: 'Guinea', GY: 'Guyana', HT: 'Haiti',
  VA: 'Vatican City', HN: 'Honduras', HU: 'Hungary', IS: 'Iceland', IN: 'India',
  ID: 'Indonesia', IR: 'Iran', IQ: 'Iraq', IE: 'Ireland', IL: 'Israel',
  IT: 'Italy', CI: "Côte d'Ivoire", JM: 'Jamaica', JP: 'Japan', KZ: 'Kazakhstan',
  JO: 'Jordan', KE: 'Kenya', KP: 'North Korea', KR: 'South Korea', KW: 'Kuwait',
  KG: 'Kyrgyzstan', LA: 'Laos', LB: 'Lebanon', LS: 'Lesotho', LV: 'Latvia',
  LR: 'Liberia', LY: 'Libya', LI: 'Liechtenstein', LT: 'Lithuania', LU: 'Luxembourg',
  MG: 'Madagascar', MW: 'Malawi', MY: 'Malaysia', MV: 'Maldives', ML: 'Mali',
  MT: 'Malta', MR: 'Mauritania', MU: 'Mauritius', MX: 'Mexico', MC: 'Monaco',
  MN: 'Mongolia', MD: 'Moldova', MA: 'Morocco', MZ: 'Mozambique', OM: 'Oman',
  NA: 'Namibia', NR: 'Nauru', NP: 'Nepal', NL: 'Netherlands', VU: 'Vanuatu',
  NZ: 'New Zealand', NI: 'Nicaragua', NE: 'Niger', NG: 'Nigeria', NO: 'Norway',
  FM: 'Micronesia', PK: 'Pakistan', PA: 'Panama', PG: 'Papua New Guinea', PY: 'Paraguay',
  PE: 'Peru', PH: 'Philippines', PL: 'Poland', PT: 'Portugal', GW: 'Guinea-Bissau',
  TL: 'Timor-Leste', QA: 'Qatar', RO: 'Romania', RU: 'Russia', RW: 'Rwanda',
  KN: 'Saint Kitts and Nevis', LC: 'Saint Lucia', VC: 'Saint Vincent and the Grenadines',
  SM: 'San Marino', ST: 'São Tomé and Príncipe', SA: 'Saudi Arabia', SN: 'Senegal',
  RS: 'Serbia', SC: 'Seychelles', SL: 'Sierra Leone', SK: 'Slovakia', SI: 'Slovenia',
  SO: 'Somalia', ZA: 'South Africa', ZW: 'Zimbabwe', ES: 'Spain', SS: 'South Sudan',
  SD: 'Sudan', SR: 'Suriname', SZ: 'Eswatini', SE: 'Sweden', CH: 'Switzerland',
  SY: 'Syria', TJ: 'Tajikistan', TH: 'Thailand', TG: 'Togo', TO: 'Tonga',
  TT: 'Trinidad and Tobago', AE: 'United Arab Emirates', TN: 'Tunisia', TR: 'Turkey',
  TM: 'Turkmenistan', UG: 'Uganda', UA: 'Ukraine', MK: 'North Macedonia', EG: 'Egypt',
  GB: 'United Kingdom', TZ: 'Tanzania', US: 'United States', UY: 'Uruguay',
  UZ: 'Uzbekistan', VE: 'Venezuela', WS: 'Samoa', YE: 'Yemen', ZM: 'Zambia',
  // entries absent from the original mapping
  BS: 'Bahamas', BZ: 'Belize', SB: 'Solomon Islands', TW: 'Taiwan',
  FK: 'Falkland Islands', TF: 'French Southern Territories', ME: 'Montenegro',
  NC: 'New Caledonia', PR: 'Puerto Rico', VN: 'Vietnam', EH: 'Western Sahara',
  BF: 'Burkina Faso',
  // disputed / unrecognized territories (no ISO numeric code in world-atlas)
  XK: 'Kosovo', XN: 'Northern Cyprus', XS: 'Somaliland',
};

/** Returns the English country name for an ISO 3166-1 alpha-2 code, or the code itself as fallback. */
export function getCountryName(iso2: string): string {
  return ISO2_TO_NAME[iso2] ?? iso2;
}

/**
 * Fix a single polygon ring that crosses the antimeridian (±180°).
 * When a ring has consecutive coordinates that jump more than 180° in longitude,
 * ECharts draws a line across the entire map. Normalizing all negative longitudes
 * to positive (adding 360°) keeps the ring contiguous on the "eastern" side.
 */
function fixRing(ring: number[][]): number[][] {
  const lngs = ring.map((p) => p[0]);
  if (Math.max(...lngs) - Math.min(...lngs) <= 180) return ring;
  return ring.map(([lng, lat]) => [lng < 0 ? lng + 360 : lng, lat]);
}

// Crimea is ~32.5–36.6°E, 44.4–46.3°N — identify its polygon within Russia's MultiPolygon
function isCrimeaPolygon(poly: number[][][]): boolean {
  const outer = poly[0];
  const lngs = outer.map((p) => p[0]);
  const lats = outer.map((p) => p[1]);
  return (
    Math.min(...lngs) >= 31 && Math.max(...lngs) <= 37.5 &&
    Math.min(...lats) >= 43 && Math.max(...lats) <= 47
  );
}

// Merge Ukraine and Crimea at the TopoJSON arc level so the shared isthmus arc
// is eliminated, producing a seamless outline with no internal border line.
function buildMergedUkraineGeometry(topology: Topology): any | null {
  const geometries = (topology.objects as any).countries.geometries;
  // IDs in the topology are strings (e.g. '643'), not numbers
  const russiaGeom = geometries.find((g: any) => String(g.id) === '643');
  const ukGeom = geometries.find((g: any) => String(g.id) === '804');

  if (!russiaGeom || !ukGeom || russiaGeom.type !== 'MultiPolygon') return null;

  const crimeaPolyArcs: number[][][][] = [];
  for (const polyRings of (russiaGeom as any).arcs as number[][][][]) {
    const tempFeat = feature(topology, { type: 'Polygon', arcs: polyRings } as any) as any;
    if (isCrimeaPolygon(tempFeat.geometry.coordinates)) {
      crimeaPolyArcs.push(polyRings);
    }
  }

  if (crimeaPolyArcs.length === 0) return null;

  try {
    // merge() in topojson-client v3 is broken; use mergeArcs + feature instead
    const mergedGeom = mergeArcs(topology, [ukGeom, { type: 'MultiPolygon', arcs: crimeaPolyArcs }] as any);
    const mergedFeat = feature(topology, mergedGeom as any) as any;
    return mergedFeat?.geometry ?? null;
  } catch {
    return null;
  }
}

// Move Crimea polygon(s) from Russia to Ukraine in the feature list.
// Crimea is always treated as Ukrainian territory: if a UA feature exists it
// receives the polygons; if not, a minimal UA feature is created for them.
// mergedUkGeom (from buildMergedUkraineGeometry) replaces Ukraine's geometry so
// the isthmus border line between mainland and Crimea is fully eliminated.
function patchCrimea(features: any[], mergedUkGeom: any | null): any[] {
  const crimeaPolygons: number[][][][] = [];

  const patched = features.map((f: any) => {
    if (f.properties?.name !== 'RU' || f.geometry?.type !== 'MultiPolygon') return f;
    const remaining = f.geometry.coordinates.filter((poly: number[][][]) => {
      if (isCrimeaPolygon(poly)) { crimeaPolygons.push(poly); return false; }
      return true;
    });
    return crimeaPolygons.length === 0 ? f : { ...f, geometry: { ...f.geometry, coordinates: remaining } };
  });

  if (crimeaPolygons.length === 0) return features;

  let uaFound = false;
  const result = patched.map((f: any) => {
    if (f.properties?.name !== 'UA') return f;
    uaFound = true;
    if (mergedUkGeom) return { ...f, geometry: mergedUkGeom };
    const geom = f.geometry;
    if (geom?.type === 'MultiPolygon') {
      return { ...f, geometry: { ...geom, coordinates: [...geom.coordinates, ...crimeaPolygons] } };
    }
    if (geom?.type === 'Polygon') {
      return { ...f, geometry: { type: 'MultiPolygon', coordinates: [geom.coordinates, ...crimeaPolygons] } };
    }
    return f;
  });

  if (!uaFound) {
    result.push({
      type: 'Feature',
      id: 804,
      properties: { name: 'UA' },
      geometry: mergedUkGeom ?? { type: 'MultiPolygon', coordinates: crimeaPolygons },
    });
  }

  return result;
}

function fixGeometry(geom: any): any {
  if (!geom) return geom;
  if (geom.type === 'Polygon') {
    return { ...geom, coordinates: geom.coordinates.map(fixRing) };
  }
  if (geom.type === 'MultiPolygon') {
    return {
      ...geom,
      coordinates: geom.coordinates.map((poly: number[][][]) =>
        poly.map(fixRing)
      ),
    };
  }
  return geom;
}

// Assign custom alpha-2 codes to territories with no ISO numeric ID in the
// world-atlas topology, identified by approximate centroid position.
function identifyUndefinedFeature(geom: any): string | undefined {
  if (!geom) return undefined;
  const ring: number[][] = geom.type === 'Polygon'
    ? geom.coordinates[0]
    : geom.coordinates?.[0]?.[0];
  if (!ring?.length) return undefined;
  const lngs = ring.map((p) => p[0]);
  const lats = ring.map((p) => p[1]);
  const midLng = (Math.min(...lngs) + Math.max(...lngs)) / 2;
  const midLat = (Math.min(...lats) + Math.max(...lats)) / 2;
  if (midLng > 19 && midLng < 22 && midLat > 41 && midLat < 44) return 'XK'; // Kosovo
  if (midLng > 32 && midLng < 35 && midLat > 34 && midLat < 37) return 'XN'; // Northern Cyprus
  if (midLng > 42 && midLng < 50 && midLat > 7  && midLat < 12) return 'XS'; // Somaliland
  return undefined;
}

/**
 * Label anchor (ECharts `properties.cp`) guaranteed to lie inside the
 * feature's largest polygon. ECharts otherwise anchors labels at the
 * bounding-rect center, which lands offshore for countries with far-flung
 * territories (US/Alaska, France/Guiana) or concave shapes (Norway, Chile).
 * Method: take the largest outer ring, scan the horizontal line through its
 * vertices' mean latitude, and use the midpoint of the widest inside segment.
 */
function computeLabelAnchor(geom: any): [number, number] | undefined {
  if (!geom) return undefined;
  const polys: number[][][][] =
    geom.type === 'Polygon' ? [geom.coordinates] : geom.type === 'MultiPolygon' ? geom.coordinates : [];
  let ring: number[][] | null = null;
  let bestArea = -1;
  for (const poly of polys) {
    const outer = poly[0];
    if (!outer?.length) continue;
    let area = 0;
    for (let i = 0, j = outer.length - 1; i < outer.length; j = i++) {
      area += outer[j][0] * outer[i][1] - outer[i][0] * outer[j][1];
    }
    area = Math.abs(area / 2);
    if (area > bestArea) {
      bestArea = area;
      ring = outer;
    }
  }
  if (!ring) return undefined;
  const y = ring.reduce((sum, p) => sum + p[1], 0) / ring.length;
  const xs: number[] = [];
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [x1, y1] = ring[j];
    const [x2, y2] = ring[i];
    if (y1 > y !== y2 > y) xs.push(x1 + ((y - y1) * (x2 - x1)) / (y2 - y1));
  }
  xs.sort((a, b) => a - b);
  let anchor: [number, number] | undefined;
  let widest = -1;
  for (let i = 0; i + 1 < xs.length; i += 2) {
    const width = xs[i + 1] - xs[i];
    if (width > widest) {
      widest = width;
      anchor = [(xs[i] + xs[i + 1]) / 2, y];
    }
  }
  return anchor;
}

let cachedGeoJson: object | null = null;

/**
 * Returns a GeoJSON FeatureCollection of world countries where each feature's
 * `properties.name` is the ISO 3166-1 alpha-2 country code.
 * Antarctica is excluded — its geometry spans the full longitude range and causes
 * horizontal rendering artifacts in ECharts' equirectangular projection.
 * Antimeridian-crossing polygons (e.g. Russia, Fiji) are normalized so ECharts
 * does not draw lines across the map.
 * Result is memoized — the conversion runs only once per page load.
 */
export function getWorldGeoJson(): object {
  if (cachedGeoJson) return cachedGeoJson;

  const geojson = feature(worldTopology, (worldTopology.objects as any).countries);
  const mergedUkGeom = buildMergedUkraineGeometry(worldTopology);

  const namedFeatures = (geojson as any).features
    .filter((f: any) => parseInt(String(f.id), 10) !== 10) // exclude Antarctica
    .map((f: any) => {
      const numId = parseInt(String(f.id), 10);
      const name = !isNaN(numId)
        ? (NUMERIC_TO_ISO2[String(numId)] ?? String(numId))
        : (identifyUndefinedFeature(f.geometry) ?? '');
      return { ...f, geometry: fixGeometry(f.geometry), properties: { name } };
    })
    .filter((f: any) => f.properties.name !== ''); // drop unidentified undefined-ID features

  cachedGeoJson = {
    ...geojson,
    // cp is computed after the Crimea patch so Ukraine's anchor reflects its
    // final merged geometry.
    features: patchCrimea(namedFeatures, mergedUkGeom).map((f: any) => ({
      ...f,
      properties: { ...f.properties, cp: computeLabelAnchor(f.geometry) },
    })),
  };

  return cachedGeoJson;
}
